import type { AnimateSpec, KeyframeEase, TransformKeyframe } from '../../../../core/render-runtime/shared/transform-keyframes.util';
import type { TextAnimSpec } from '../../../../core/render-runtime/shared/text-animation.util';
import type { TextBubbleStyle } from '../../../../core/render-runtime/shared/text-bubble.util';
import type { EditorDrawingLayer } from './editor-drawing.model';
import type { EditorTextDesignPreset, EditorTextDesignTextOverrides } from './editor-text-design.model';
import type { AudioCleanupParameters } from '../../../../core/render-runtime/shared/audio-cleanup';
import type { MediaEnhancements } from '../../../../core/render-runtime/shared/media-enhancements.util';
import type { TextStyleKeyframe } from '../../../../core/render-runtime/shared/text-style-keyframes.util';
import type { MaskKeyframe, MaskPoint, MaskShape } from '../../../../core/render-runtime/shared/media-mask.util';
import type { AdjustKeyframe } from '../../../../core/render-runtime/shared/media-adjust-keyframes.util';

export type { TransformKeyframe, AnimateSpec } from '../../../../core/render-runtime/shared/transform-keyframes.util';
export type { TextStyleKeyframe } from '../../../../core/render-runtime/shared/text-style-keyframes.util';
export type { MaskKeyframe, MaskPoint, MaskShape } from '../../../../core/render-runtime/shared/media-mask.util';
export type { AdjustKeyframe } from '../../../../core/render-runtime/shared/media-adjust-keyframes.util';

export type EditorClipKind = 'video' | 'image' | 'audio' | 'text' | 'caption' | 'effect' | 'transition' | 'sticker' | 'beat' | 'motion' | 'drawing';

export interface EditorTransform {
  x: number;
  y: number;
  /**
   * Uniform scale multiplier (the historical field + the corner-drag handle).
   * Stays the single source of truth for every existing clip and corner resize.
   * When `scaleX`/`scaleY` are null/absent BOTH axes fall back to this, so a clip
   * without the per-axis fields renders byte-for-byte identically to before.
   */
  scale: number;
  /**
   * Independent HORIZONTAL scale (width). Null/absent → falls back to the uniform
   * `scale`. Set only by edge-handle (ml/mr) resizes. Additive — serializes free
   * via the clip `{...c}` spread.
   */
  scaleX?: number | null;
  /**
   * Independent VERTICAL scale (height). Null/absent → falls back to the uniform
   * `scale`. Set only by edge-handle (tc/bc) resizes.
   */
  scaleY?: number | null;
  rotation: number;
  opacity: number;
}

/**
 * Effective per-axis scale for an EditorTransform: the SINGLE shared helper every
 * site (canvas overlay handles, preview compositor, render adapter) must use so
 * preview and export can never drift on non-uniform resizes.
 *
 * sx = scaleX ?? scale ?? 1, sy = scaleY ?? scale ?? 1. A clip with no per-axis
 * fields therefore yields sx === sy === scale (today's behaviour exactly).
 */
export function effectiveScaleXY(
  transform: { scale?: number | null; scaleX?: number | null; scaleY?: number | null } | null | undefined
): { sx: number; sy: number } {
  const uniform = transform?.scale;
  const base = typeof uniform === 'number' && Number.isFinite(uniform) ? uniform : 1;
  const rawX = transform?.scaleX;
  const rawY = transform?.scaleY;
  const sx = typeof rawX === 'number' && Number.isFinite(rawX) ? rawX : base;
  const sy = typeof rawY === 'number' && Number.isFinite(rawY) ? rawY : base;
  return { sx, sy };
}

/** Vertical (top→bottom) gradient fill for the text glyphs. Overrides the solid `color` when set. */
export interface EditorTextGradient {
  /** Top color stop. */
  from: string;
  /** Bottom color stop. */
  to: string;
  /** Optional middle color stop (0.5) for a 3-stop gradient (e.g. gold sheen). */
  via?: string | null;
}

/** Neon / outer glow painted as a blurred halo around the glyphs (before the fill). */
export interface EditorTextGlow {
  color: string;
  /** 0..1 halo opacity. Absent ⇒ 0.6. */
  strength?: number;
  /** 0..1 halo spread/blur. Absent ⇒ `strength`, preserving existing presets. */
  range?: number;
  /** Horizontal halo offset in px at the 1080-wide reference. */
  offsetX?: number;
  /** Vertical halo offset in px at the 1080-wide reference. */
  offsetY?: number;
}

/** Configurable soft drop shadow rendered behind the glyphs. */
export interface EditorTextShadow {
  color: string;
  /** 0..1 shadow opacity. Absent ⇒ 0.65. */
  opacity?: number;
  /** 0..1 shadow blur. Absent ⇒ 0.45. */
  blur?: number;
  /** Shadow distance in px at the 1080-wide reference. Absent ⇒ 8. */
  distance?: number;
  /** Direction in degrees: 0 = right, 90 = down. Absent ⇒ 45. */
  angle?: number;
}

/** Hard offset / 3D-extrude shadow behind the glyphs — distinct from the soft legibility shadow. */
export interface EditorTextShadow3D {
  color: string;
  /** Horizontal offset in px at the 1080-wide reference (scaled by output width). Absent ⇒ 0. */
  dx?: number;
  /** Vertical offset in px at the 1080-wide reference. Absent ⇒ ~9% of font size. */
  dy?: number;
}

/** Text/caption styling for the Text tool. All fields optional — sensible render defaults apply. */
export interface EditorTextStyle {
  /** Font size in px at a 1080px-wide reference; the renderer scales it by (canvasWidth / 1080). */
  fontSize?: number;
  /** 400 = normal, 700/800 = bold. */
  fontWeight?: number;
  fontStyle?: 'normal' | 'italic';
  /** Multiplier relative to font size. */
  lineHeight?: number;
  /** Text arc strength in degrees from -360..360. Absent means straight text. */
  curve?: number;
  /** Text-layer blend opacity (0..1). Absent preserves the legacy fully-opaque appearance. */
  blendOpacity?: number;
  align?: 'left' | 'center' | 'right';
  /** Background box color, or null/undefined for none. */
  background?: string | null;
  /** Background box alpha, independent of text opacity (0..1). */
  backgroundOpacity?: number;
  /** Flat background-box controls. Absent values preserve the legacy automatic box. */
  backgroundRoundness?: number;
  backgroundWidth?: number;
  backgroundHeight?: number;
  /** Background offsets in px at the 1080-wide reference. */
  backgroundOffsetX?: number;
  backgroundOffsetY?: number;
  stroke?: string | null;
  strokeWidth?: number;
  /** Gradient text fill (gold/chrome/sunset…). Absent ⇒ solid `color`. */
  gradient?: EditorTextGradient | null;
  /** Neon/outer glow around the glyphs. Absent ⇒ none. */
  glow?: EditorTextGlow | null;
  /** Soft configurable drop shadow. Absent ⇒ legacy/default text shadow behavior. */
  shadow?: EditorTextShadow | null;
  /** 3D / retro offset shadow behind the glyphs. Absent ⇒ none. */
  shadow3d?: EditorTextShadow3D | null;
  /** Speech-bubble / callout SHAPE behind the text (Bubbles tab). Absent ⇒ flat box via `background`.
   * `colorTo` makes the fill a vertical gradient; `shadow` adds a soft (glass/clay) or hard (neubrutalist) drop shadow. */
  bubble?: TextBubbleStyle | null;
  /** Letter-by-letter animation (typewriter / wave / pop…). Absent ⇒ whole-block animation only. */
  textAnim?: TextAnimSpec | null;
  /** Overall caption scale multiplier (Review parity, 0.7–1.8). */
  sizePercent?: number;
  /**
   * Display font FAMILY from the editor font library (e.g. "Bebas Neue") — loaded into
   * the document for preview and shipped to the export worker via RenderTimeline.fonts.
   * Absent = the system stack (today's behaviour). `language: 'ur'` still wins for Urdu.
   */
  fontFamily?: string;
  language?: 'en' | 'ur' | null;
  /** Applied one-click style preset id. */
  preset?: string;
}

export type EditorStickerPrimitive = 'emoji' | 'badge' | 'callout' | 'label-strip' | 'arrow' | 'shape';

export type EditorStickerStyle =
  | 'solid'
  | 'outline'
  | 'soft'
  | 'duotone'
  | 'shadow'
  | 'gradient'
  | 'neon'
  | 'glass'
  | 'sketch'
  | 'bold';

/** Sticker primitive + params (Stickers tool) — lifted from the Review feature-catalog. */
export interface EditorStickerParams {
  primitive: EditorStickerPrimitive;
  color?: string;
  /** Text color for text primitives; secondary/accent color for vector shapes. */
  color2?: string;
  /** Relative scale 0..1. */
  size?: number;
  /** Rotation in degrees (arrow). */
  angle?: number;
  /** Glyph (emoji) or label text (badge/callout/strip). */
  text?: string;
  /** Declarative visual form within a primitive (for example ticket, speech-left, star-8). */
  variant?: string;
  /** Modern fill/stroke treatment shared by procedural vector presets. */
  style?: EditorStickerStyle;
  /** Optional outline width multiplier used by vector presets. */
  strokeWidth?: number;
}

export type EditorEffectPrimitive =
  | 'wash' | 'tint' | 'duotone' | 'vignette' | 'border-pulse' | 'radial-pulse'
  | 'zoom-pulse' | 'shake' | 'flash' | 'scanlines' | 'grain' | 'rays' | 'blur-edges' | 'chroma-shift'
  | 'text-fireworks-ii' | 'filter';

export type EditorEffectDetectionTarget =
  | 'person-mask'
  | 'body-contour'
  | 'pose-landmarks'
  | 'face-landmarks';

/** Full-frame visual effect + params (Effects tool) — lifted from the Review feature-catalog. */
export interface EditorEffectParams {
  primitive: EditorEffectPrimitive;
  /** Browsing/render intent. Omitted legacy effects remain full-frame video effects. */
  scope?: 'video' | 'body';
  /** Target hint used by subject-reactive body shaders. */
  target?: 'frame' | 'person';
  /** Concrete human-vision output required by this preset. */
  detectionTarget?: EditorEffectDetectionTarget;
  /** Missing detection skips the effect; body effects never fall back to full-frame rendering. */
  requiresPerson?: boolean;
  color?: string;
  color2?: string;
  /** Base strength 0..1. */
  opacity?: number;
  /** Animation speed multiplier. */
  speed?: number;
  /** Degrees (wash/rays direction). */
  angle?: number;
  /** Relative scale (vignette radius, border width, etc.) 0..1. */
  size?: number;
  blend?: 'screen' | 'overlay' | 'multiply' | 'normal';
  /** User-set intensity 0..1, multiplied with opacity at render. */
  intensity?: number;
  /**
   * Optional WebGL effect/LUT shader id (e.g. 'fisheye', 'teal-orange'). When set, the
   * renderer applies that GLSL shader to the frame via the WebGL pass instead of the
   * Canvas-2D primitive. `primitive` stays a valid value as the 2D fallback.
   */
  glId?: string;
  /** Optional per-preset overrides consumed by shader runners that expose custom uniforms. */
  glUniforms?: Record<string, number | number[]>;
  /**
   * Runtime-resolved, digest-verified independent scene program. Signed catalog
   * JSON cannot inject this field directly; the download service attaches it.
   */
  sceneProgram?: import('../../../../core/render-runtime/shared/effect-scene-program').EffectSceneProgram;
  /** Durable release URL for an image plate sampled as the effect's second texture. */
  sceneAssetUrl?: string;
  /** Stable identity used to dedupe the image inside an export job. */
  sceneAssetId?: string;
  sceneAssetWidth?: number;
  sceneAssetHeight?: number;
  /** CPU fallback selects the same physical aperture as the GPU scene program. */
  sceneConcept?: number;
  /** Timed Filters-tool metadata and trusted Adjust channels. */
  filterId?: string;
  filterStrength?: number;
  brightness?: number;
  contrast?: number;
  saturation?: number;
  temperature?: number;
  blur?: number;
  vignette?: number;
  hue?: number;
}

export type EditorTransitionPrimitive =
  | 'fade' | 'wipe' | 'slide-band' | 'zoom-hit' | 'spin-hint'
  | 'glitch-bars' | 'flash-cut' | 'blinds' | 'pixel-blocks' | 'iris';

/** Transition overlay + params (Transitions tool) — lifted from the Review feature-catalog. */
export interface EditorTransitionParams {
  primitive: EditorTransitionPrimitive;
  color?: string;
  color2?: string;
  opacity?: number;
  speed?: number;
  angle?: number;
  size?: number;
  blend?: 'screen' | 'overlay' | 'multiply' | 'normal';
  /**
   * Optional gl-transition shader id (e.g. 'Directional'). When set, the renderer
   * blends the two neighbour clips with that GLSL shader via the WebGL pass instead
   * of the Canvas-2D blend. `primitive` stays a real blend (usually 'fade') so the
   * worker resolves the from/to frames and the live editor preview has a 2D fallback.
   */
  glId?: string;
}

export type EditorBeatPrimitive =
  | 'waveform' | 'beat-marker' | 'snap-pulse' | 'edge-flash' | 'bar-bounce' | 'shake-sync'
  | 'oscilloscope' | 'stereo-ribbon' | 'liquid-wave' | 'lissajous'
  | 'radial-spectrum' | 'spectrum-arc' | 'spectrum-spiral' | 'frequency-rings'
  | 'particle-burst' | 'particle-vortex' | 'confetti-cannon' | 'rain-field'
  | 'ink-ripple' | 'cloth-grid' | 'plasma-field' | 'metaballs'
  | 'hud-radar' | 'data-grid' | 'equalizer-crown' | 'ring-stack'
  | 'light-rays' | 'lightning' | 'prism-fan' | 'laser-grid'
  | 'kaleidoscope' | 'mandala' | 'orbital-rings' | 'starfield-3d'
  | 'tunnel-3d' | 'terrain-3d' | 'city-3d' | 'helix-3d';

export type EditorBeatMaterial = 'solid' | 'neon' | 'glass' | 'metal' | 'hologram' | 'ink' | 'plasma';
export type EditorBeatAudioMap = 'amp' | 'bass' | 'mid' | 'treble' | 'spectrum' | 'onset';
export type EditorBeatLayout = 'center' | 'bottom' | 'full' | 'radial' | 'perspective';

/** Beat overlay + params (Beats tool) — lifted from the Review feature-catalog beat primitives. */
export interface EditorBeatParams {
  primitive: EditorBeatPrimitive;
  color?: string;
  color2?: string;
  opacity?: number;
  speed?: number;
  size?: number;
  /** Renderer-authored geometry variant. It changes construction, not merely color. */
  variant?: number;
  /** Stable seed for deterministic particles/procedural geometry in preview and export. */
  seed?: number;
  density?: number;
  depth?: number;
  symmetry?: number;
  material?: EditorBeatMaterial;
  audioMap?: EditorBeatAudioMap;
  layout?: EditorBeatLayout;
}

/** Source-rectangle CROP on a media clip — normalized 0..1 of the source frame (Crop tool). */
export interface EditorCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Color ADJUST on a media clip — applied via ctx.filter (+ temp/vignette overlays), CPU+GPU (Adjust + Filters tools). */
export interface EditorAdjust {
  /** 1 = neutral. */
  brightness?: number;
  contrast?: number;
  saturation?: number;
  /** -1..1 warm(+)/cool(-) tint; 0 = neutral. */
  temperature?: number;
  /** Gaussian blur px at the 1080 reference; 0 = none. */
  blur?: number;
  /** 0..1 edge darkening. */
  vignette?: number;
  /** 0..1 hue-rotate fraction (0 = none) for creative filters. */
  hue?: number;
  /** The one-tap Filter preset this adjust came from (UI metadata; renderer ignores it). */
  presetId?: string;
  /** Filter strength 0..1 the preset was applied at (UI metadata; values are pre-lerped). */
  strength?: number;
}

/** CHROMA-KEY (green-screen removal) on a media clip (Chroma tool). */
export interface EditorChroma {
  /** Key color to remove, e.g. '#00d400'. */
  color: string;
  /** 0..1 — how close a pixel must be to the key to drop. */
  similarity?: number;
  /** 0..1 — edge softness. */
  smoothness?: number;
  /** 0..1 — spill (green fringe) suppression. */
  spill?: number;
}

/** Shape MASK on a media clip (Mask tool). Geometry normalized 0..1 (center + size). */
export interface EditorMask {
  /** Stable id allows several independently selected/keyframed masks on one clip. */
  id?: string;
  shape: MaskShape;
  /** 0..1 edge feather. */
  feather?: number;
  invert?: boolean;
  /** Center + size, normalized 0..1 of the frame. Defaults to a centered 60% box. */
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  /** Direction (deg) for linear/radial. */
  angle?: number;
  /** Optional matte drawn where this clip is masked out. Omit for a transparent mask. */
  fillColor?: string | null;
  /** 0..1 opacity for the optional coloured matte. */
  fillOpacity?: number;
  text?: string;
  fontFamily?: string;
  fontWeight?: number;
  points?: MaskPoint[];
  brushSize?: number;
  keyframes?: MaskKeyframe[] | null;
}

/** SPEED CURVE — keyframed playback rate over the clip (cinematic ramps). Absent = constant `playbackRate`. */
export interface EditorSpeedCurve {
  /** Points at clip-relative fraction t (0..1) with a rate multiplier; sorted by t. */
  points: { t: number; rate: number; ease?: KeyframeEase }[];
}

/**
 * BLUR/ERASE region (Erase tool) — censors a part of the clip (face/logo/watermark) with a
 * gaussian blur or pixelate mosaic. Geometry normalized 0..1 (center + size) of the frame,
 * exactly like EditorMask. A clip can carry several regions. Pure FE canvas pass, applied by
 * BOTH the live preview and the export worker (preview == export).
 */
export interface EditorBlurRegion {
  shape?: 'rectangle' | 'ellipse';
  /** Center + size, normalized 0..1 of the frame. Defaults to a centered 30% box. */
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  /** 'blur' = gaussian censor (default), 'pixelate' = mosaic blocks. */
  mode?: 'blur' | 'pixelate';
  /** 0..1 intensity (blur radius / mosaic block size). Default 0.5. */
  strength?: number;
  /** 0..1 soft edge. Default 0.15. */
  feather?: number;
}

export type EditorMotionTrackingTarget = 'face' | 'person' | 'region';
export type EditorMotionTrackingSampleState = 'detected' | 'interpolated' | 'held';

/** A user-authored tracking box, normalized to the source frame. x/y are its centre. */
export interface EditorObjectTrackingRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface EditorMotionTrackingSample {
  /** Seconds from the ATTACHED clip's start. */
  tSeconds: number;
  /** Subject centre, normalized to the source frame. */
  centerX: number;
  centerY: number;
  /** Relative subject size in the source frame. */
  scale: number;
  /** Subject orientation in degrees (eyes for a face, shoulders for a person). */
  rotation: number;
  confidence: number;
  state?: EditorMotionTrackingSampleState;
}

export interface EditorMotionTrackingCorrection {
  id: string;
  /** Seconds from the ATTACHED clip's start. */
  tSeconds: number;
  /** Additive local correction in editor percent coordinates. */
  x: number;
  y: number;
  /** Multiplicative local correction; 1 is neutral. */
  scale: number;
  /** Additive local correction in degrees. */
  rotation: number;
}

/**
 * Browser-authored motion tracking attached to an existing visual clip. The data is
 * deliberately JSON-only and clip-relative so move/duplicate/undo/persistence are free.
 */
export interface EditorMotionTracking {
  version: 1;
  sourceClipId: string;
  sourceFingerprint: string;
  target: EditorMotionTrackingTarget;
  /** Initial custom-object box. Present when target is `region`. */
  targetRegion?: EditorObjectTrackingRegion;
  /** Defaults to the whole attached clip; blur regions remain inside their media clip. */
  attachment?: {
    kind: 'clip' | 'blur-region';
    blurRegionIndex?: number;
    baseWidth?: number;
    baseHeight?: number;
  };
  follow: {
    position: boolean;
    scale: boolean;
    rotation: boolean;
  };
  range: {
    startSeconds: number;
    endSeconds: number;
  };
  samples: EditorMotionTrackingSample[];
  corrections: EditorMotionTrackingCorrection[];
  /** Keeps the element's chosen placement relative to the first reliable detection. */
  attachmentOffset: {
    x: number;
    y: number;
    scale: number;
    rotation: number;
  };
  smoothing: number;
  stale?: boolean;
  staleReason?: string | null;
  /** Clip-relative areas that have not been analyzed after an extension. */
  uncoveredRanges?: Array<{ startSeconds: number; endSeconds: number }>;
}

export interface EditorCatalogReference {
  releaseId: string;
  group: string;
  itemId: string;
  itemVersion: number;
}

export interface EditorClip {
  id: string;
  kind: EditorClipKind;
  /**
   * Optional provenance for an item instantiated from a signed catalogue release.
   * Complete render params/keyframes remain embedded on the clip, so offline and
   * old saved projects never depend on this reference being resolvable.
   */
  catalogRef?: EditorCatalogReference | null;
  assetId?: string | null;
  trackId: string;
  startSeconds: number;
  endSeconds: number;
  sourceStartSeconds?: number | null;
  sourceEndSeconds?: number | null;
  title: string;
  text?: string | null;
  color?: string | null;
  volume?: number | null;
  muted?: boolean;
  /**
   * Audio fade-in ramp (seconds) from the clip's start: gain rises 0→1 over this
   * window. Null/absent/≤0 = no fade-in (today's behaviour). Additive — serializes
   * free via the `{...c}` spread.
   */
  fadeInSeconds?: number | null;
  /**
   * Audio fade-out ramp (seconds) to the clip's end: gain falls 1→0 over this
   * window. Null/absent/≤0 = no fade-out (today's behaviour).
   */
  fadeOutSeconds?: number | null;
  /**
   * Per-clip volume envelope: gain multiplier keyframes at CLIP-RELATIVE times
   * (seconds from the clip start), sorted ascending by `tSeconds`. Gain is
   * linearly interpolated between points; before the first / after the last point
   * the nearest point's gain holds. Null/absent/empty = flat gain (today's
   * behaviour). Stacks multiplicatively with volume + fades.
   */
  volumeEnvelope?: { tSeconds: number; gain: number; ease?: KeyframeEase }[] | null;
  /**
   * When true on a music/soundtrack clip, this clip's audio is ducked (attenuated)
   * under any voice/dialogue (non-music) clip windows at render. Absent/false =
   * no ducking (today's behaviour). The duck curve is synthesized by the render
   * adapter from the voice clip windows.
   */
  duckUnderVoice?: boolean;
  audioCleanup?: AudioCleanupParameters | null;
  /** Per-clip playback speed (1 = normal); applied to source-time mapping at render. */
  playbackRate?: number;
  /**
   * How source pixels fill the clip box:
   * cover = fill and center-crop, contain = show all with letterboxing,
   * stretch = fill both axes without preserving the source aspect ratio.
   */
  fitMode?: 'cover' | 'contain' | 'stretch';
  /**
   * SPEED CURVE (Speed tool) — keyframed rate ramp integrated over clip time (cinematic
   * slow-mo / speed ramps). Null/absent = constant `playbackRate` (today's behaviour).
   * Additive — serializes free via the clip `{...c}` spread.
   */
  speedCurve?: EditorSpeedCurve | null;
  /** Source-rect CROP (Crop tool) — normalized 0..1 sub-region of the frame. Null/absent = full frame. */
  crop?: EditorCrop | null;
  /** Color ADJUST / one-tap filter look (Adjust + Filters tools). Null/absent = untouched pixels. */
  adjust?: EditorAdjust | null;
  /** Clip-relative keyframes for the complete Adjust grade. */
  adjustKeyframes?: AdjustKeyframe[] | null;
  /** Local visual processing shared by preview/export (Blend, Stabilize, Enhance, Denoise). */
  mediaEnhancements?: MediaEnhancements | null;
  /** CHROMA-KEY green-screen removal (Chroma tool). Null/absent = none. */
  chroma?: EditorChroma | null;
  /** Shape MASK (Mask tool). Null/absent = none. */
  mask?: EditorMask | null;
  /** Multiple independently editable masks. `mask` remains as a legacy first-mask fallback. */
  masks?: EditorMask[] | null;
  /**
   * BLUR/ERASE regions (Erase tool) — censor patches (blur/pixelate) over parts of the
   * frame. Null/absent/empty = none (today's behaviour, byte-for-byte). Additive —
   * serializes free via the clip `{...c}` spread.
   */
  blurRegions?: EditorBlurRegion[] | null;
  transform?: EditorTransform | null;
  /**
   * KEYFRAMED transform animation (Animate tool). Each entry sets any subset of
   * channels (x/y/scale/scaleX/scaleY/rotation/opacity) at a CLIP-RELATIVE time;
   * a channel with no keyframe holds its `transform` (base) value. Null/absent/
   * empty = the static `transform` only (today's behaviour, byte-for-byte). The
   * resolver's fast path returns the base transform untouched when this is empty
   * and both animateIn/animateOut are null, so existing clips cost nothing extra.
   * Additive — serializes free via the clip `{...c}` spread.
   */
  keyframes?: TransformKeyframe[] | null;
  /** Optional frontend-only tracking path composed with creative Animate keyframes. */
  motionTracking?: EditorMotionTracking | null;
  /**
   * Cheap ENTRANCE animation (fadeIn/slideUp/popIn/zoomIn/…) over `durationSeconds`
   * from the clip's start. Null/absent = no entrance (today's behaviour). Folded
   * into the resolved transform by the shared resolver in BOTH compositors.
   */
  animateIn?: AnimateSpec | null;
  /** Cheap EXIT animation over `durationSeconds` into the clip's end. Null/absent = none. */
  animateOut?: AnimateSpec | null;
  /** Text/caption styling (Text tool). */
  textStyle?: EditorTextStyle | null;
  /** Clip-relative keyframes for numeric and color text-style properties. */
  textStyleKeyframes?: TextStyleKeyframe[] | null;
  /**
   * Layered Text Design selected from the design-first library. The whole preset is
   * persisted with the clip so saved projects do not depend on a future catalog
   * revision. `text` remains the editable primary slot; `textDesignText` overrides
   * the other semantic slots without mutating the embedded preset or its layers.
   */
  textDesign?: EditorTextDesignPreset | null;
  /** Editable non-primary copy for layered designs. `text` remains the primary slot. */
  textDesignText?: EditorTextDesignTextOverrides | null;
  /** Serializable vector artwork authored by the Draw tool. */
  drawing?: EditorDrawingLayer | null;
  /** Sticker primitive + params (Stickers tool). */
  sticker?: EditorStickerParams | null;
  /** Full-frame visual effect + params (Effects tool). */
  effect?: EditorEffectParams | null;
  /** Transition overlay + params (Transitions tool). */
  transition?: EditorTransitionParams | null;
  /** Beat overlay + params (Beats tool). */
  beat?: EditorBeatParams | null;
  /**
   * MOTION clip (Animate tool, timeline-clip model): a standalone `kind:'motion'`
   * clip on an 'animation' track that carries NO media. During its [start,end] window
   * it animates the BASE visual layer via its `keyframes` (authored around identity —
   * x/y offset %, scale mult, rotation deg, opacity), composed onto the base clip's
   * transform in BOTH compositors. `motionPreset` is the applied preset id (timeline
   * label + re-tap). User drops it at the playhead (5s default), resizes its edges,
   * and can stack multiple. Additive; serializes free via the clip spread.
   */
  motionPreset?: string | null;
  metadata?: Record<string, unknown>;
}
